import React, { FC, useMemo } from 'react'
import './hero-section-front.module.scss'
import { sanitizeHTML } from '../../../../utils/common-utils'
import { HeroSectionFrontProps } from './HeroSectionFront.types'
import { getFacetImagePath, getFacetImagePathForIA } from '../../../../utils/background-util'

export const HeroSectionFront: FC<HeroSectionFrontProps> = props => {
  const { content, testId } = props
  const {
    heading,
    body,
    image,
    imageMobile,
    textAlignment,
    facetColor,
    hideMobileImage,
    fontColor,
    contentModifier,
    buttonAlignment,
    buttonHref,
    buttonTarget,
    buttonText,
    buttonLinkAltText,
    noClip,
    analytics,
    brandExtension,
    contentWidth,
    useLegacyHero,
  } = content
  const legacyHeader = useLegacyHero ? 'Legacy--hero' : ''
  const isIA = window.location.href.includes('impact-analyzer')
  const showMobileImage = hideMobileImage !== 'true'
  let largeFacet, mobileFacet
  if (isIA) {
    largeFacet = getFacetImagePathForIA(facetColor, { isMobile: false })
    mobileFacet = getFacetImagePathForIA(facetColor, { isMobile: true })
  } else {
    largeFacet = getFacetImagePath(facetColor, { isMobile: false })
    mobileFacet = getFacetImagePath(facetColor, { isMobile: true })
  }

  const isCI = window.location.href.includes('/competitive-intelligence')

  const textWrapperAlignment = useMemo(() => {
    if (textAlignment?.includes('u-textCenter')) {
      return 'u-flexAlignItemsCenter'
    }
    if (textAlignment?.includes('u-textRight')) {
      return 'u-flexAlignItemsEnd'
    }
    return ''
  }, [textAlignment])

  return (
    <section
      className={`HeroSectionFront ${brandExtension} ${legacyHeader}`}
      data-analytics-container='Hero'
      data-analytics-component-template='Section Front'
      data-analytics-component-name='Hero'
      data-testid='hero-section-front'
    >
      <img
        className={`${isCI ? 'HeroSectionFront-facet-stretched' : 'HeroSectionFront-facet'} ${
          noClip ? ' HeroSectionFront-facet--noClip' : ''
        } u-sm-hidden`}
        src={largeFacet}
        alt=''
        data-testid={testId ? `${testId}-facet` : null}
      />
      <img
        className={`${isCI ? 'HeroSectionFront-facet-stretched' : 'HeroSectionFront-facet'} ${
          noClip ? ' HeroSectionFront-facet--noClip' : ''
        } u-md-hidden u-lg-hidden`}
        src={mobileFacet}
        alt=''
        data-testid={testId ? `${testId}-facet-mobile` : null}
      />

      <div className='HeroSectionFront-content'>
        <div
          className={`HeroSectionFront-heading u-sizeConstrained u-flex u-flexCol ${legacyHeader} ${textWrapperAlignment}`}
        >
          {heading && (
            <h1
              className={`Type-Display ${
                useLegacyHero && !isIA ? 'u-paddingTop96' : 'u-paddingTop48'
              } ${fontColor} ${textAlignment} ${contentWidth}`}
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(heading) }}
              data-testid={testId ? `${testId}-heading` : null}
            ></h1>
          )}
          {body && (
            <div
              className={`Content Content--large u-paddingTop8 ${contentModifier} ${textAlignment} ${contentWidth}`}
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(body) }}
              data-testid={testId ? `${testId}-body` : null}
            ></div>
          )}
          {buttonText && (
            <a
              className={`Button Button--primary Button--reversed u-marginTop24 ${buttonAlignment}`}
              href={buttonHref}
              target={buttonTarget}
              aria-label={buttonLinkAltText || ''}
              data-analytics={analytics}
            >
              {buttonText}
            </a>
          )}
        </div>
        <div className='u-paddingBottom48 u-sm-paddingHoriz16 u-paddingTop48 u-posRelative'>
          {image && (
            <img
              className='HeroSectionFront-featuredImage u-sm-hidden u-block u-md-sizeFull u-sizeConstrained'
              src={image}
              alt=''
              data-testid={testId ? `${testId}-image` : null}
            />
          )}
          {showMobileImage && (
            <img
              className='HeroSectionFront-featuredImage u-md-hidden u-lg-hidden u-block u-sizeConstrained'
              src={imageMobile}
              alt=''
              data-testid={testId ? `${testId}-image-mobile` : null}
            />
          )}
        </div>
      </div>
    </section>
  )
}
